// ---------------------------------------------------------------------------
// Firebase Realtime Database — REST client (หน้าเว็บอ่าน/เขียนตรง ไม่มี server)
//  - get/put/patch/remove/update(multi-path) + query (orderBy/equalTo/limit/startAt/endAt)
//  - transaction: GET พร้อม ETag แล้ว PUT ด้วย if-match ; ชนกัน (412) ลองใหม่อัตโนมัติ
//  - createOnly: PUT ด้วย if-match: null_etag (เขียนได้เฉพาะตอนยังไม่มีข้อมูล) ใช้ทำ idempotency
//  - ถ้า query ด้วยฟิลด์ที่ยังไม่ได้ตั้ง .indexOn (rules ยังไม่ได้วาง) จะถอยไปอ่านทั้งโหนดแล้วกรองในเครื่อง
//  - key ใน RTDB ห้ามมี . $ # [ ] / -> ใช้ fk() แปลง id ที่มาจากผู้ใช้/ภายนอกก่อนต่อเป็น path เสมอ
// ---------------------------------------------------------------------------
import { FB_URL, FB_ROOT } from "./config";

export class FbError extends Error {
  constructor(code, message) {
    super(message || code);
    this.name = "FbError";
    this.code = code;
  }
}

/** แปลง id เป็น key ที่ RTDB รับได้ (ไม่เปลี่ยน id ปกติ เช่น ct0abc.. / uuid) */
export const fk = (id) => encodeURIComponent(String(id ?? "")).replace(/\./g, "%2E");

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const segs = (path) => String(path || "").split("/").filter((s) => s !== "");
const fullSegs = (path) => [...segs(FB_ROOT), ...segs(path)];
const urlOf = (path, query) => {
  // root ต้องเป็น "<url>/.json" — ถ้าตัด / ทิ้ง จะกลายเป็นโดเมน "…app.json" ที่ไม่มีอยู่จริง
  const base = `${FB_URL}/${fullSegs(path).map(encodeURIComponent).join("/")}`;
  const qs = Object.entries(query || {})
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&");
  return `${base}.json${qs ? "?" + qs : ""}`;
};

// ---- query ------------------------------------------------------------------
const typeRank = (v) => (v === null || v === undefined ? 0 : v === false ? 1 : v === true ? 2 : typeof v === "number" ? 3 : typeof v === "string" ? 4 : 5);
const cmpVal = (a, b) => {
  const ra = typeRank(a), rb = typeRank(b);
  if (ra !== rb) return ra - rb;
  if (ra === 3) return a - b;
  if (ra === 4) return a < b ? -1 : a > b ? 1 : 0;
  return 0;
};
const orderValue = (orderBy, key, value) => (orderBy === "$key" ? key : orderBy === "$value" ? value : value && typeof value === "object" ? value[orderBy] : undefined);

/** ใช้ query กับ object ในเครื่อง (ใช้ตอน fallback และใน unit test) -> [[key, value], ...] เรียงตาม orderBy */
export function applyQuery(obj, q = {}) {
  let rows = Object.entries(obj || {});
  const ob = q.orderBy || "$key";
  const ov = (r) => orderValue(ob, r[0], r[1]);
  rows.sort((a, b) => cmpVal(ov(a), ov(b)) || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  if (q.equalTo !== undefined) rows = rows.filter((r) => cmpVal(ov(r), q.equalTo) === 0 && typeRank(ov(r)) === typeRank(q.equalTo));
  if (q.startAt !== undefined) rows = rows.filter((r) => cmpVal(ov(r), q.startAt) >= 0);
  if (q.endAt !== undefined) rows = rows.filter((r) => cmpVal(ov(r), q.endAt) <= 0);
  if (q.limitToFirst) rows = rows.slice(0, q.limitToFirst);
  if (q.limitToLast) rows = rows.slice(-q.limitToLast);
  return rows;
}

const queryParams = (q = {}) => {
  const p = {};
  if (q.orderBy) p.orderBy = JSON.stringify(q.orderBy);
  ["equalTo", "startAt", "endAt"].forEach((k) => {
    if (q[k] !== undefined) p[k] = JSON.stringify(q[k]);
  });
  if (q.limitToFirst) p.limitToFirst = q.limitToFirst;
  if (q.limitToLast) p.limitToLast = q.limitToLast;
  if (q.shallow) p.shallow = "true";
  return p;
};

// ---- HTTP -------------------------------------------------------------------
async function raw(method, path, { query, body, headers } = {}) {
  let res;
  try {
    res = await fetch(urlOf(path, query), {
      method,
      cache: "no-store",
      headers: { ...(body !== undefined ? { "Content-Type": "application/json" } : {}), ...(headers || {}) },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch (e) {
    throw new FbError("fb_unavailable", "เชื่อมต่อฐานข้อมูลไม่สำเร็จ ตรวจสอบอินเทอร์เน็ตแล้วลองใหม่");
  }
  let text = "";
  try {
    text = await res.text();
  } catch (e) {
    text = "";
  }
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch (e) {
      data = null;
    }
  }
  return { status: res.status, ok: res.ok, headers: res.headers, data, text };
}

const fail = (r) => {
  if (r.status === 401 || r.status === 403) return new FbError("fb_denied", "ฐานข้อมูลไม่อนุญาต (ตรวจ database.rules.json ที่วางใน Firebase console)");
  const msg = (r.data && r.data.error) || r.text || `HTTP ${r.status}`;
  return new FbError("fb_error", `ฐานข้อมูลผิดพลาด: ${String(msg).slice(0, 160)}`);
};

let warned = false;
const INDEX_ERR = /index\s*not\s*defined|indexon/i;

export const fb = {
  /** อ่านค่า ณ path (null ถ้าไม่มี) ; query = {orderBy, equalTo, startAt, endAt, limitToFirst, limitToLast} */
  async get(path, query) {
    const r = await raw("GET", path, { query: queryParams(query) });
    if (r.status === 400 && query && query.orderBy && query.orderBy !== "$key" && INDEX_ERR.test(r.text)) {
      if (!warned && typeof console !== "undefined") {
        warned = true;
        console.warn("[AITalk] ยังไม่ได้วาง database.rules.json (.indexOn) — กำลังกรองข้อมูลในเครื่องแทน ช้ากว่าปกติ");
      }
      const all = await fb.get(path);
      return Object.fromEntries(applyQuery(all, query));
    }
    if (!r.ok) throw fail(r);
    return r.data;
  },

  /** query แล้วคืนเป็นลิสต์ [[key, value], ...] เรียงตาม orderBy */
  async entries(path, query) {
    const data = await fb.get(path, query);
    if (!data || typeof data !== "object") return [];
    return applyQuery(data, { orderBy: query && query.orderBy });
  },

  /** รายชื่อ key ใต้ path (ไม่ดึงค่า) */
  async keys(path) {
    const d = await fb.get(path, { shallow: true });
    return d && typeof d === "object" ? Object.keys(d) : [];
  },

  async put(path, value) {
    const r = await raw("PUT", path, { body: value === undefined ? null : value, query: { print: "silent" } });
    if (!r.ok) throw fail(r);
  },

  /** merge ระดับฟิลด์ (ค่า null = ลบฟิลด์) */
  async patch(path, obj) {
    const r = await raw("PATCH", path, { body: obj, query: { print: "silent" } });
    if (!r.ok) throw fail(r);
  },

  async remove(path) {
    const r = await raw("DELETE", path, { query: { print: "silent" } });
    if (!r.ok) throw fail(r);
  },

  /** เขียนหลาย path ในคำขอเดียว (atomic) : { "a/b": v, "c/d": null } */
  async update(map) {
    const r = await raw("PATCH", "", { body: map, query: { print: "silent" } });
    if (!r.ok) throw fail(r);
  },

  /** เขียนเฉพาะตอนยังไม่มีข้อมูล -> true = เขียนสำเร็จ, false = มีอยู่แล้ว */
  async createOnly(path, value) {
    // หมายเหตุ: Firebase ไม่ให้ใช้ print=silent ร่วมกับ if-match
    const r = await raw("PUT", path, { body: value, headers: { "if-match": "null_etag" } });
    if (r.status === 412) return false;
    if (!r.ok) throw fail(r);
    return true;
  },

  /**
   * transaction แบบ ETag: fn(current|null) -> ค่าใหม่ | undefined (ยกเลิก) ; fn โยน error ได้ (ยกเลิกพร้อม error)
   * คืน {committed, value}
   */
  async transaction(path, fn, { retries = 12 } = {}) {
    for (let i = 0; i < retries; i++) {
      const r = await raw("GET", path, { headers: { "X-Firebase-ETag": "true" } });
      if (!r.ok) throw fail(r);
      const etag = r.headers && r.headers.get ? r.headers.get("ETag") : null;
      if (!etag) throw new FbError("fb_error", "ฐานข้อมูลไม่ส่ง ETag กลับมา");
      const cur = r.data === undefined ? null : r.data;
      const next = await fn(cur);
      if (next === undefined) return { committed: false, value: cur };
      const w = await raw("PUT", path, { body: next, headers: { "if-match": etag } });
      if (w.status === 412) {
        await sleep(15 + Math.random() * 60 * (i + 1));
        continue;
      }
      if (!w.ok) throw fail(w);
      return { committed: true, value: next };
    }
    throw new FbError("conflict", "ระบบกำลังยุ่ง (มีการแก้ไขชนกัน) ลองใหม่อีกครั้ง");
  },
};

export default fb;
